'use client';

import React, { useEffect } from 'react';
import { Card, CardMode } from './Card';

/**
 * Modal component properties
 */
export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  mode?: CardMode;
  'aria-labelledby'?: string;
  'aria-describedby'?: string;
}

/**
 * Modal dialog component with overlay and card container
 * @param {ModalProps} props - Modal properties
 * @returns {JSX.Element | null} Modal overlay with card content or null when closed
 */
export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  children,
  mode = 'primary',
  'aria-labelledby': ariaLabelledby,
  'aria-describedby': ariaDescribedby,
}) => {
  useEffect(() => {
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEsc);

    return () => {
      window.removeEventListener('keydown', handleEsc);
    };
  }, [onClose]);

  if (!isOpen) return null;

  return (
    <div
      x-component-name="Modal"
      className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 transition-opacity duration-300"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby={ariaLabelledby}
      aria-describedby={ariaDescribedby}
    >
      <div
        className="relative w-full max-w-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <Card mode={mode} className="w-full">
          {children}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </Card>
      </div>
    </div>
  );
};